import React from "react";
import { Link } from "react-router-dom";

import { Button } from "../../design-system/components/Button";
import { ErrorState } from "../../design-system/components/StatePanel";
import { LanguageContext } from "../../localization/LanguageContext";
import "./ModuleRuntimeBoundary.css";

/**
 * Keeps an unexpected module-render failure inside the authenticated shell.
 *
 * The boundary intentionally renders no exception text: component names,
 * stack traces and provider responses do not belong in an operator-facing
 * screen. React still reports the original error to the development console,
 * while the agent receives a clear recovery action and can continue working
 * elsewhere in the CRM.
 */
export default class ModuleRuntimeBoundary extends React.Component {
  static contextType = LanguageContext;
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Preserve normal React development diagnostics without placing private
    // runtime details in the UI. Production telemetry can subscribe to this
    // event later without changing the safe fallback contract.
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.error("CRM module render failed", error, info);
    }
  }

  componentDidUpdate(previousProps) {
    // Moving to another module must always clear a failure from the previous
    // one. This also means browser Back/Forward navigation remains usable.
    if (previousProps.resetKey !== this.props.resetKey && this.state.error) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ error: null });
    }
  }

  retry = () => {
    this.setState({ error: null });
  };

  render() {
    const { children, moduleName = "this module" } = this.props;
    if (!this.state.error) return children;
    const copy = this.context?.copy || ((value, variables = {}) => String(value).replace(
      /\{([^}]+)\}/g,
      (match, key) => Object.prototype.hasOwnProperty.call(variables, key) ? variables[key] : match
    ));
    const moduleLabel = this.context?.moduleLabel || ((value) => value);
    const localizedModuleName = moduleLabel(moduleName);

    return (
      <section className="crm-module-runtime-boundary" aria-label={copy("{moduleName} recovery", { moduleName: localizedModuleName })}>
        <ErrorState
          title={copy("{moduleName} needs to be reopened", { moduleName: localizedModuleName })}
          description={copy("This workspace did not finish loading safely. No ticket, customer, connection, or account data was changed. Retry the module, or continue from another workspace.")}
          actions={(
            <div className="crm-module-runtime-boundary__actions">
              <Button type="button" onClick={this.retry}>{copy("Retry this module")}</Button>
              <Link className="crm-button" data-variant="secondary" data-size="medium" to="/Dashboard">
                <span className="crm-button__label">{copy("Open Dashboard")}</span>
              </Link>
              <Link className="crm-button" data-variant="ghost" data-size="medium" to="/AccountSetup">
                <span className="crm-button__label">{moduleLabel("Account Setup")}</span>
              </Link>
            </div>
          )}
        />
      </section>
    );
  }
}
